export const GENERATED_DOCUMENT_DOCX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const GENERATED_DOCUMENT_TYPES = [
  'QME_REPORT',
  'SIBTF_REPORT',
  'PR2',
  'PI_REPORT',
  'PR1',
  'PR4_REPORT',
  'RFA',
  'PTP_REPORT',
  'INJECTION',
  'WORK_STATUS',
] as const;

export type GeneratedDocumentType = (typeof GENERATED_DOCUMENT_TYPES)[number];

/**
 * Runtime guard for the websocket event parser.
 *
 * Derived from the tuple above rather than a hand-copied array literal: the
 * parser used to hold its own duplicate list, so a new document type was
 * accepted by TypeScript and then silently dropped at runtime with no log —
 * meaning the tab never refreshed when its DOCX finished.
 */
export function isGeneratedDocumentType(
  value: unknown,
): value is GeneratedDocumentType {
  return (
    typeof value === 'string' &&
    (GENERATED_DOCUMENT_TYPES as readonly string[]).includes(value)
  );
}

export type GeneratedDocumentOwnerType = 'SESSION' | 'AI_SCRIBE';

export type GeneratedDocumentSourceArtifactType =
  | 'OUTPUT'
  | 'AI_SCRIBE_CONTENT';

export type GeneratedDocumentVersionKind = 'GENERATED_BASELINE' | 'USER_SAVE';

export type GeneratedDocumentGenerationStateValue =
  | 'IN_PROGRESS'
  | 'FAILED'
  | 'AVAILABLE';

export type GeneratedDocumentDraftStatus =
  | 'ACTIVE'
  | 'STALE_BASE_VERSION'
  | 'SAVED'
  | 'DISCARDED';

export interface GeneratedDocumentVersion {
  id: string;
  versionNumber: number;
  kind: GeneratedDocumentVersionKind;
  mimeType: string | null;
  fileSizeBytes: number | null;
  createdAt: string;
}

export interface GeneratedDocumentListItem {
  id: string;
  sessionId: string;
  documentType: GeneratedDocumentType;
  isEditable: boolean;
  ownerType: GeneratedDocumentOwnerType;
  ownerId: string;
  activeVersion: GeneratedDocumentVersion;
  createdAt: string;
  updatedAt: string | null;
}

export interface GeneratedDocumentLatestGenerationJob {
  id: string;
  status: string;
  errorCode: string | null;
  failedAt: string | null;
  retryExhaustedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface GeneratedDocumentGenerationState {
  generatedDocumentId: string;
  documentType: GeneratedDocumentType;
  ownerType: GeneratedDocumentOwnerType;
  ownerId: string;
  sourceArtifactType: GeneratedDocumentSourceArtifactType;
  sourceArtifactId: string;
  state: GeneratedDocumentGenerationStateValue;
  activeVersionId: string | null;
  latestJob: GeneratedDocumentLatestGenerationJob;
}

export interface GeneratedDocumentListResponse {
  items: GeneratedDocumentListItem[];
  generationStates?: GeneratedDocumentGenerationState[];
}

// Phase 9 editing flows will use draft and lock summaries from the detail API.
export interface GeneratedDocumentDraftSummary {
  id: string;
  baseVersionId: string;
  status: GeneratedDocumentDraftStatus;
  revision: number;
  lastAutosavedAt: string | null;
  updatedAt: string | null;
}

export interface GeneratedDocumentLockSummary {
  id: string;
  draftId: string | null;
  lockedByUserId: string;
  editorSessionId: string;
  expiresAt: string;
  heartbeatAt: string | null;
  createdAt: string;
}

export interface GeneratedDocumentDetail extends GeneratedDocumentListItem {
  activeDraft: GeneratedDocumentDraftSummary | null;
  lock: GeneratedDocumentLockSummary | null;
}

export interface GeneratedDocumentVersionsResponse {
  items: GeneratedDocumentVersion[];
}

export interface GeneratedDocumentVersionSignedUrlResponse {
  documentId: string;
  versionId: string;
  versionNumber: number;
  mimeType: string | null;
  signedUrl: string;
  expiresAt: string;
}

export interface AcquireGeneratedDocumentLockRequest {
  editorSessionId: string;
  takeover?: boolean;
}

export interface GeneratedDocumentLockOperationRequest {
  editorSessionId: string;
  lockToken: string;
}

export interface GeneratedDocumentLockAcquireResponse {
  lock: GeneratedDocumentLockSummary;
  draft: GeneratedDocumentDraftSummary;
  lockToken: string;
  draftReadSignedUrl: string;
  draftReadSignedUrlExpiresAt: string;
  openedExistingDraft: boolean;
}

export interface GeneratedDocumentLockHeartbeatResponse {
  lock: GeneratedDocumentLockSummary;
}

export interface GeneratedDocumentLockReleaseResponse {
  released: true;
}

export interface InsertGeneratedDocumentSignatureRequest
  extends GeneratedDocumentLockOperationRequest {
  lockId: string;
  signatureId: string;
}

export interface GeneratedDocumentSignatureInsertResponse {
  /** Five-minute read URL for the signature image. */
  url: string;
  /** ISO time of the SIGNATURE_INSERTED audit event; shown under the image. */
  insertedAt: string;
}

export interface GeneratedDocumentDraftLockRequest {
  editorSessionId: string;
  lockToken: string;
  expectedRevision: number;
}

export interface CreateGeneratedDocumentDraftUploadRequest extends GeneratedDocumentDraftLockRequest {}

export interface CompleteGeneratedDocumentDraftUploadRequest extends GeneratedDocumentDraftLockRequest {
  fileSizeBytes?: number;
  contentHashSha512?: string;
}

export interface SaveGeneratedDocumentDraftRequest extends GeneratedDocumentDraftLockRequest {
  uploadId?: string;
  fileSizeBytes?: number;
  contentHashSha512?: string;
}

export interface DiscardGeneratedDocumentDraftRequest extends GeneratedDocumentDraftLockRequest {}

export interface GeneratedDocumentDraftUploadResponse {
  uploadId: string;
  uploadUrl: string;
  fields: Record<string, string>;
  expiresAt: string;
  maxFileSizeBytes: number;
}

export interface GeneratedDocumentDraftUploadCompleteResponse {
  completed: true;
  draft: GeneratedDocumentDraftSummary;
  nextUpload: GeneratedDocumentDraftUploadResponse | null;
}

export interface GeneratedDocumentDraftSaveResponse {
  saved: true;
  version: GeneratedDocumentVersion;
  draft: GeneratedDocumentDraftSummary;
  released: true;
}

export interface GeneratedDocumentDraftDiscardResponse {
  discarded: true;
  draft: GeneratedDocumentDraftSummary;
  released: true;
}

interface GeneratedDocumentChangedEventBase {
  session_id: string;
  generated_document_id: string;
  generated_document_type: GeneratedDocumentType;
}

export interface GeneratedDocumentBaselineRegisteredEvent extends GeneratedDocumentChangedEventBase {
  event_type: 'BASELINE_REGISTERED' | 'USER_VERSION_SAVED';
  active_version_id: string;
  version_number: number;
}

export interface GeneratedDocumentBaselineFailedEvent extends GeneratedDocumentChangedEventBase {
  event_type: 'BASELINE_GENERATION_FAILED';
  error_code: string;
}

export type GeneratedDocumentChangedEvent =
  | GeneratedDocumentBaselineRegisteredEvent
  | GeneratedDocumentBaselineFailedEvent;
